import en from './locales/en.js';
import zhHant from './locales/zhHant.js';

/**
 * Interface language.
 *
 * Hand-written rather than a library, for the same reason the lint rules are: what this actually
 * needs is small, and a dependency would bring plural machinery that Chinese does not use and that
 * English already handles here in fifteen lines.
 *
 * THE ENGLISH IS THE FALLBACK, NEVER A KEY. A missing translation shows the English sentence, not
 * `record.late.title`. A half-translated screen is ordinary; a screen with dotted identifiers on it
 * looks broken, and the person seeing it cannot tell which of the two it is.
 *
 * Rolled out module by module. Anything not yet in the dictionaries keeps showing English, so the
 * app is usable at every point in the migration rather than only at the end.
 */

export const LANGUAGES = [
  // `short` is for the collapsed sidebar rail, where there is room for a badge and not a word.
  // Written in its own language for the same reason `label` is: somebody who cannot read the
  // interface has to recognise it without reading the interface.
  { code: 'en', label: 'English', short: 'EN', htmlLang: 'en' },
  { code: 'zh-Hant', label: '繁體中文', short: '繁', htmlLang: 'zh-Hant-HK' },
];

const DICTIONARIES = { en, 'zh-Hant': zhHant };

export const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'portal.language';

const isKnown = (code) => LANGUAGES.some(l => l.code === code);

/** Walks a dotted path. Returns undefined rather than throwing on a missing branch. */
const lookup = (dict, path) => path.split('.').reduce((node, part) => (
  node && typeof node === 'object' ? node[part] : undefined
), dict);

/**
 * `{name}` and `{count}` in a string are replaced from the values given.
 *
 * Deliberately not a template engine: no expressions, no formatting, no conditionals. Anything that
 * needs a decision belongs in the code, where it can be read, not inside a translated string where
 * a translator would have to understand it to keep it working.
 */
const fill = (text, vars) => String(text).replace(/\{(\w+)\}/g, (whole, key) => (
  Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : whole
));

/**
 * One string or two, depending on how many.
 *
 * A dictionary entry is either `'Save'` or `{ one: '{count} student', other: '{count} students' }`.
 * Chinese has no plural forms, so its entries are plain strings and `other` is all that is read --
 * which is why this is a shape in the dictionary rather than a rule in the code.
 */
const pick = (entry, vars) => {
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
    const n = Number(vars.count);
    if (n === 1 && entry.one !== undefined) return entry.one;
    return entry.other !== undefined ? entry.other : entry.one;
  }
  return entry;
};

// Reported once per key per session. A translator needs the list; a user must never see it, and a
// console line per render would be noise nobody reads.
const warned = new Set();

// `fallback` is for text that already exists in the code and has not been moved into a dictionary
// yet -- the module names in Layout are the case this was written for. It keeps a missing key
// showing the real English words rather than an identifier, which matters most during a rollout
// that is deliberately module by module.
export const translate = (language, path, vars = {}, fallback) => {
  const dict = DICTIONARIES[language] || DICTIONARIES[DEFAULT_LANGUAGE];
  let entry = lookup(dict, path);
  if (entry === undefined && language !== DEFAULT_LANGUAGE) {
    entry = lookup(DICTIONARIES[DEFAULT_LANGUAGE], path);
    if (entry !== undefined && !warned.has(`${language}:${path}`)) {
      warned.add(`${language}:${path}`);
      console.info(`[i18n] ${language} has no "${path}" -- showing English`);
    }
  }
  // Still nothing: the key itself is wrong. Show the last segment rather than the whole path, so a
  // mistake reads as an odd label instead of a stack trace on the screen.
  if (entry === undefined) {
    if (!warned.has(`missing:${path}`)) {
      warned.add(`missing:${path}`);
      console.warn(`[i18n] no such key "${path}"`);
    }
    return fallback !== undefined ? fill(fallback, vars) : path.split('.').pop();
  }
  return fill(pick(entry, vars), vars);
};
